import {
  GeneralTenantCase,
  GeneralTenantCaseFilter,
  MietendeckelCaseFilter,
  MietminderungCaseFilter,
  MietpreisbremseCaseFilter,
  Profile,
  RentalObject,
  User,
} from "@happy-legal/graphql-types";
import { Box, Button, Collapse } from "@mui/material";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  StatusBadge,
  ActionButton,
  EmptyState,
  LoadingRow,
} from "../components/table/StyledTable";
import TableToolbar from "../components/table/TableToolbar";
import TablePaginationBar from "../components/table/TablePaginationBar";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FilterIcon from "@mui/icons-material/FilterListOutlined";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { GeneralTenantCaseTypeEnum } from "../../generalTenant/enums";
import { isGeneralTenantCase } from "../../generalTenant/helpers";
import MUIRouterLink from "../../shared/components/MUIRouterLink";
import AddressCard from "../components/Addresses/AddressCard";
import CasesFilter from "../components/CasesFilter";
import FormattedDateTimeTooltip from "../components/FormattedDateTimeTooltip";
import { ProductTypeEnum, StatusEnum } from "../enums";
import { getStatusVariant, hasReferrer, isReport, isSure } from "../helpers";
import { CaseContext } from "../providers/CaseProvider";
import { LoadingContext } from "../providers/LoadingProvider";
import { DELETE_CASE, GET_ALL_CASES } from "../reducer";

type CaseFilter =
  | MietminderungCaseFilter
  | MietendeckelCaseFilter
  | MietpreisbremseCaseFilter
  | GeneralTenantCaseFilter;

/**
 * Table overview of all cases, filled via the query prop passed to it.
 * @param allCasesQuery getAllCase query
 * @param deleteMutation delete case mutation
 */
const Cases = ({
  allCasesQuery,
  deleteMutation,
}: {
  allCasesQuery: string;
  deleteMutation: string;
}) => {
  // Load and destructure required global state objects
  const { state, dispatch } = useContext(CaseContext);

  const items = state.allCases?.items;
  const total = state.allCases?.total ?? 0;

  // Load pathname to create links with id query strings appended
  const { pathname } = useLocation();

  // Component states for controlling table layout and query filter
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [filter, setFilter] = useState<CaseFilter>({});
  const [notEqual, setNotEqual] = useState<CaseFilter>({});
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Helpers
  const { isLoading } = useContext(LoadingContext);
  const hasResults = !!items?.length;
  const hasGTCs = items?.some((item) => isGeneralTenantCase(item));
  const hasReports = items?.some((item) => isReport(item));
  const activeFilterCount = Object.keys(filter).length;

  // Table header titles
  const titles = [
    "Fall-Nr.",
    "Anschrift",
    ...(hasGTCs ? ["Anfragetyp"] : ["Produkttyp"]),
    "Status",
    ...(hasReports || hasGTCs ? ["Bezahlt am"] : []),
    "Erstellt am",
  ];

  const getAllCases = () =>
    dispatch({
      type: GET_ALL_CASES,
      query: allCasesQuery,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      ...(Object.keys(filter).length && { filter }),
      ...(Object.keys(notEqual).length && { notEqual }),
    });

  // Reducer dispatch called on render and when table/filter changes
  useEffect(() => {
    (async () => await getAllCases())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, rowsPerPage, page, filter]);

  // Delete case based on id, after confirm and refresh all cases
  const handleDelete = async (idToDelete: string) => {
    if (!(await isSure())) return;
    dispatch({
      type: DELETE_CASE,
      mutation: deleteMutation,
      id: idToDelete,
    });

    if (items?.every((item) => item.id !== idToDelete)) return;
    await getAllCases();
  };

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        backgroundColor: "background.paper",
        overflow: "hidden",
      }}
    >
      <TableToolbar
        count={total}
        label="Fälle"
        actions={
          <Button
            variant={filtersOpen ? "contained" : "outlined"}
            startIcon={<FilterIcon />}
            onClick={() => setFiltersOpen((open) => !open)}
          >
            Filter{activeFilterCount ? ` (${activeFilterCount})` : ""}
          </Button>
        }
      />

      <Collapse in={filtersOpen} unmountOnExit>
        <Box
          sx={{
            px: 2,
            py: 2.5,
            backgroundColor: "background.subtle",
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <CasesFilter
            filter={filter}
            setFilter={setFilter}
            notEqual={notEqual}
            setNotEqual={setNotEqual}
          />
        </Box>
      </Collapse>

      <TableContainer sx={{ border: 0, borderRadius: 0 }}>
        <Table>
          <TableHead>
            <TableRow>
              {titles.map((title) => (
                <TableCell key={title}>{title}</TableCell>
              ))}
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <LoadingRow colSpan={titles.length + 1} />
            ) : hasResults ? (
              // Sort items newest-oldest before rendering
              items
                ?.sort((a, b) => Date.parse(b!.created!) - Date.parse(a!.created!))
                ?.map((item) => {
                  const {
                    id,
                    caseId,
                    created,
                    productType,
                    referrer,
                    rentalObject,
                    profile,
                    status,
                    inputCompleted,
                  } = item;

                  const user = (profile as any as Profile)?.user as any as User;

                  const address = {
                    ...(rentalObject as any as RentalObject)?.address,
                    emailAddress: user?.email,
                    phoneNumber: user?.phone_number,
                  };

                  return (
                    <TableRow key={id}>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        <MUIRouterLink to={`${pathname}?id=${id}`}>
                          {caseId}
                        </MUIRouterLink>
                      </TableCell>
                      <TableCell>
                        <AddressCard address={address} />
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        {hasGTCs
                          ? GeneralTenantCaseTypeEnum[
                              //@ts-ignore - enum declaration cannot be used from types
                              (item as GeneralTenantCase)
                                .generalTenantCaseType as GeneralTenantCaseTypeEnum
                            ]
                          : ProductTypeEnum[productType]}
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          variant={getStatusVariant(status)}
                          status={
                            //@ts-ignore - string indexing without interface
                            StatusEnum[status] ?? status
                          }
                        />
                      </TableCell>
                      {(hasReports || hasGTCs) && (
                        <TableCell sx={{ color: "text.secondary" }}>
                          {hasReferrer(item) ? (
                            referrer?.referringPartner
                          ) : inputCompleted ? (
                            <FormattedDateTimeTooltip date={inputCompleted} />
                          ) : (
                            "noch nicht"
                          )}
                        </TableCell>
                      )}
                      <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                        <FormattedDateTimeTooltip date={created} />
                      </TableCell>
                      <TableCell align="right">
                        <ActionButton
                          icon={<DeleteIcon />}
                          label="Fall löschen"
                          onClick={() => handleDelete(id)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
            ) : (
              <EmptyState
                message="Keine Fälle gefunden."
                colSpan={titles.length + 1}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePaginationBar
        count={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(next) => {
          setRowsPerPage(next);
          setPage(0);
        }}
      />
    </Box>
  );
};

export default Cases;
